import { useState } from "react";

export interface DayInfo {
  date: string; // "YYYY-MM-DD" already in the display timezone
  status: "up" | "down" | "degraded" | "no-data";
  upCount?: number;
  downCount?: number;
}

interface UptimeBarProps {
  days: DayInfo[];
  uptimePct: number;
  timezone?: string;
}

const statusColors: Record<string, string> = {
  up:        "#39D98A",
  degraded:  "#FDDD48",
  down:      "#FF5C5C",
  "no-data": "rgba(255,255,255,0.08)",
};

function formatDate(iso: string): string {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function getMonthLabel(iso: string): string {
  const d = new Date(iso + "T12:00:00Z");
  return d.toLocaleDateString(undefined, { month: "short", timeZone: "UTC" });
}

function pctColor(pct: number): string {
  if (pct >= 99) return "#39D98A";
  if (pct >= 95) return "#FDDD48";
  return "#FF5C5C";
}

export default function UptimeBar({ days, uptimePct, timezone: _timezone }: UptimeBarProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const monthLabelPositions: number[] = [];
  for (let i = 0; i < days.length; i++) {
    if (days[i].date.slice(8, 10) === "01") {
      monthLabelPositions.push(i);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#98A1AB" }}>90-day uptime</span>
        <span style={{ fontSize: 20, fontWeight: 800, color: pctColor(uptimePct) }}>
          {uptimePct.toFixed(2)}%
        </span>
      </div>

      <div style={{ position: "relative" }}>
        <div style={{ display: "flex", gap: 2, alignItems: "stretch" }}>
          {days.map((day, i) => (
            <div
              key={i}
              style={{ flex: 1, position: "relative" }}
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              <div
                style={{
                  height: 40,
                  background: statusColors[day.status],
                  borderRadius: 2,
                  cursor: "default",
                  opacity: hoveredIdx === i ? 0.7 : 1,
                  transition: "opacity 0.1s",
                }}
              />
              {hoveredIdx === i && (
                <div
                  style={{
                    position: "absolute",
                    bottom: "calc(100% + 8px)",
                    left: i < days.length / 2 ? 0 : "auto",
                    right: i >= days.length / 2 ? 0 : "auto",
                    background: "#121212",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 3,
                    padding: "7px 11px",
                    fontSize: 12,
                    color: "#EFF2F5",
                    whiteSpace: "nowrap",
                    zIndex: 20,
                    pointerEvents: "none",
                    lineHeight: 1.6,
                  }}
                >
                  <div style={{ fontWeight: 700, color: "#EFF2F5" }}>{formatDate(day.date)}</div>
                  <div style={{ color: statusColors[day.status] }}>
                    {day.status === "up" ? "Operational" : day.status === "down" ? "Outage" : day.status === "degraded" ? "Degraded" : "No data"}
                  </div>
                  {day.status !== "no-data" && day.upCount != null && day.downCount != null && (
                    <div style={{ color: "#98A1AB", fontSize: 11, marginTop: 2 }}>
                      {(100 * day.upCount / (day.upCount + day.downCount)).toFixed(1)}% uptime
                      <span style={{ color: "#98A1AB", marginLeft: 5, opacity: 0.6 }}>
                        ({day.upCount + day.downCount} checks)
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div style={{ position: "relative", height: 20, marginTop: 4 }}>
          {monthLabelPositions.map((pos) => {
            const pct = (pos / (days.length - 1)) * 100;
            return (
              <span
                key={pos}
                style={{
                  position: "absolute",
                  left: `${pct}%`,
                  transform: "translateX(-50%)",
                  fontSize: 10,
                  color: "#98A1AB",
                  fontWeight: 600,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  pointerEvents: "none",
                }}
              >
                {getMonthLabel(days[pos].date)}
              </span>
            );
          })}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
          <span style={{ fontSize: 11, color: "#98A1AB", opacity: 0.6 }}>90 days ago</span>
          <span style={{ fontSize: 11, color: "#98A1AB", opacity: 0.6 }}>Today</span>
        </div>
      </div>
    </div>
  );
}
